import type { NavigateFunction } from "react-router-dom";
import { store } from "../redux/store";
import {
  appendHobbyPersonCompletedResult,
  appendHobbyPersonHobbySelection,
  appendHobbyPersonImage,
  initHobbyPeopleSession,
  setHobbyGroupPhotoFlow,
  setHobbyPersonHobbySelectionAt,
  setHobbyPersonImageAt,
  setHobbyPersonImages,
  setUploadedImageUrl,
} from "../redux/slices/uploadFlowSlice";
import type { HobbyPersonHobbySelection } from "../redux/slices/uploadFlowSlice";
import { extractPersonsFromGroup } from "../services/caricatureHobbyService";
import {
  persistHobbyGenerationResult,
  type HobbyGenerationResult,
} from "./hobbyGenerationNavigation";

export function getHobbyPeopleCount(): number {
  return store.getState().uploadFlow.hobbyPeopleCount ?? 1;
}

/** Index of the person currently in progress (0-based). */
export function getActiveHobbyPersonIndex(): number {
  return store.getState().uploadFlow.hobbyPersonCompletedResults.length;
}

export function getCurrentCapturePersonNumber(): number {
  const total = getHobbyPeopleCount();
  return Math.min(getActiveHobbyPersonIndex() + 1, total);
}

export function getCurrentHobbySelectionPersonNumber(): number {
  return getActiveHobbyPersonIndex() + 1;
}

export function isMultiPersonHobbySession(): boolean {
  return getHobbyPeopleCount() > 1;
}

export function allHobbyPersonsCompleted(): boolean {
  const { hobbyPeopleCount, hobbyPersonCompletedResults } = store.getState().uploadFlow;
  const total = hobbyPeopleCount ?? 1;
  return hobbyPersonCompletedResults.length >= total;
}

/** After one person's image is ready (camera or QR), go straight to hobby selection for that person. */
export function finishHobbyCaricaturePersonCapture(
  navigate: NavigateFunction,
  stationSlug: string,
  imageUrl: string,
  options?: { onCloseModal?: () => void }
) {
  const personIndex = getActiveHobbyPersonIndex();
  const { hobbyPersonImages } = store.getState().uploadFlow;

  if (personIndex < hobbyPersonImages.length) {
    store.dispatch(setHobbyPersonImageAt({ index: personIndex, imageUrl }));
  } else {
    store.dispatch(appendHobbyPersonImage(imageUrl));
  }

  store.dispatch(setUploadedImageUrl(imageUrl));
  options?.onCloseModal?.();
  navigate(`/${stationSlug}/select-hobby`, {
    state: { capturedImage: imageUrl, currentPersonIndex: personIndex },
  });
}

export type GroupPersonCountMismatch = {
  expected: number;
  found: number;
};

function resolveExtractedPersonCount(
  result: { imageUrls: string[]; data?: any; results?: any[] }
): number {
  // Prefer successfully extracted image URLs — that is what we proceed with.
  if (result.imageUrls.length > 0) return result.imageUrls.length;

  const data = result.data;
  const fromData = Number(
    data?.detected_count ??
      data?.detected_persons ??
      data?.persons_found ??
      data?.person_count ??
      data?.count
  );
  if (Number.isFinite(fromData) && fromData > 0) return fromData;

  const resultsLen = Array.isArray(result.results) ? result.results.length : 0;
  return resultsLen > 0 ? resultsLen : 0;
}

/**
 * Extract people from one group photo, seed the multi-person hobby session
 * from the API result, then open hobby selection for the first extracted person.
 * If an expected personCount is provided, it must match the extracted count.
 */
const MAX_GROUP_PHOTO_PEOPLE = 6;

export async function finishHobbyGroupPhotoCapture(
  navigate: NavigateFunction,
  stationSlug: string,
  groupImageSource: string,
  options?: {
    onCloseModal?: () => void;
    onError?: (message: string) => void;
    onLoadingChange?: (loading: boolean) => void;
    onPersonCountMismatch?: (info: GroupPersonCountMismatch) => void;
    /** Called when extract succeeds with more than 6 people — do not navigate. */
    onTooManyPeople?: (found: number) => void;
    /** Optional expected count; when set, must match API extract count. */
    personCount?: number;
  }
): Promise<boolean> {
  options?.onLoadingChange?.(true);
  try {
    const expectedCount =
      options?.personCount && options.personCount > 0
        ? options.personCount
        : undefined;

    // Allow detecting > 6 so we can block and ask the user to retry.
    const result = await extractPersonsFromGroup(groupImageSource, {
      maxWorkers: 3,
      maxPersons: expectedCount || MAX_GROUP_PHOTO_PEOPLE + 1,
    });
    if (!result.success || !result.imageUrls.length) {
      options?.onError?.(
        result.error || "Could not extract people from the group photo. Please try again."
      );
      return false;
    }

    const foundCount = resolveExtractedPersonCount(result);
    if (foundCount > MAX_GROUP_PHOTO_PEOPLE) {
      if (options?.onTooManyPeople) {
        options.onTooManyPeople(foundCount);
      } else {
        options?.onError?.(
          `We found ${foundCount} people in the photo. Please use a photo with no more than ${MAX_GROUP_PHOTO_PEOPLE} people.`
        );
      }
      return false;
    }

    if (expectedCount && expectedCount > 0 && foundCount !== expectedCount) {
      const mismatch = { expected: expectedCount, found: foundCount };
      if (options?.onPersonCountMismatch) {
        options.onPersonCountMismatch(mismatch);
      } else {
        options?.onError?.(
          `You selected ${mismatch.expected} people, but we found ${mismatch.found} in the photo. Please try again.`
        );
      }
      return false;
    }

    const imageUrls = result.imageUrls;
    const sessionCount = foundCount > 0 ? foundCount : imageUrls.length;
    store.dispatch(initHobbyPeopleSession(sessionCount));
    store.dispatch(setHobbyPersonImages(imageUrls.slice(0, sessionCount)));
    store.dispatch(setHobbyGroupPhotoFlow(true));
    store.dispatch(setUploadedImageUrl(imageUrls[0]));
    options?.onCloseModal?.();
    navigate(`/${stationSlug}/select-hobby`, {
      replace: true,
      state: {
        capturedImage: imageUrls[0],
        currentPersonIndex: 0,
        fromGroupPhoto: true,
      },
    });
    return true;
  } catch (e: any) {
    options?.onError?.(
      e?.message || "Could not extract people from the group photo. Please try again."
    );
    return false;
  } finally {
    options?.onLoadingChange?.(false);
  }
}

export function saveHobbyPersonHobbySelection(
  personIndex: number,
  selection: HobbyPersonHobbySelection
): HobbyPersonHobbySelection[] {
  const { hobbyPersonHobbySelections } = store.getState().uploadFlow;

  if (personIndex < hobbyPersonHobbySelections.length) {
    store.dispatch(setHobbyPersonHobbySelectionAt({ index: personIndex, selection }));
    return hobbyPersonHobbySelections.map((item, idx) =>
      idx === personIndex ? selection : item
    );
  }

  store.dispatch(appendHobbyPersonHobbySelection(selection));
  return [...hobbyPersonHobbySelections, selection];
}

export function navigateToNextPersonCapture(
  navigate: NavigateFunction,
  stationSlug: string
): void {
  const { hobbyGroupPhotoFlow, hobbyPersonImages } = store.getState().uploadFlow;
  const nextIndex = getActiveHobbyPersonIndex();
  const existingImage = hobbyPersonImages?.[nextIndex];

  // Group-photo sessions already have every person cropped — skip re-capture.
  if (hobbyGroupPhotoFlow && existingImage) {
    store.dispatch(setUploadedImageUrl(existingImage));
    navigate(`/${stationSlug}/select-hobby`, {
      replace: true,
      state: { capturedImage: existingImage, currentPersonIndex: nextIndex, fromGroupPhoto: true },
    });
    return;
  }

  navigate(`/${stationSlug}/select-media-library`, { replace: true });
}

/** Store this person's chosen caricature, then start the next person or open final combine. */
export function saveHobbyPersonResultAndContinue(
  navigate: NavigateFunction,
  routeSlug: string,
  selectedImageUrl: string,
  stationData?: Record<string, unknown>
): void {
  if (!selectedImageUrl?.trim()) return;

  const { hobbyPeopleCount, hobbyPersonCompletedResults } = store.getState().uploadFlow;
  const total = hobbyPeopleCount ?? 1;

  store.dispatch(appendHobbyPersonCompletedResult(selectedImageUrl.trim()));
  const completedCount = hobbyPersonCompletedResults.length + 1;

  if (completedCount < total) {
    navigateToNextPersonCapture(navigate, routeSlug);
    return;
  }

  const allCharacterUrls = [...hobbyPersonCompletedResults, selectedImageUrl.trim()];
  const combinedResult: HobbyGenerationResult = { imageUrls: allCharacterUrls };
  persistHobbyGenerationResult(combinedResult);
  navigate(`/${routeSlug}/created-person-hobby`, {
    replace: true,
    state: {
      generationResult: combinedResult,
      stationData,
      isFinalCombine: true,
    },
  });
}
